import { ActionList, BlockStack, Box, Button, IndexTable, InlineStack, Modal, Popover, Spinner, Text, TextField } from '@shopify/polaris';
import { useCallback, useMemo, useState } from 'react';
import { useNotification } from 'context/NotificationContext';
import { useGetUsers } from 'queries/users.query';
import { useGetTrip_manifest_dispatch_list, useMutateAssign_driver_to_trip, useMutateRemove_driver_from_trip } from 'queries/trip_manifest.query';
import { useRequestAiDispatch } from 'queries/ai.query';
import { MagicIcon, PlusIcon, ResetIcon } from '@shopify/polaris-icons';

type DispatchModalProps = {
  open: boolean;
  departureDate: string;
  activeJourneyId: string;
  onClose: () => void;
};

/** Khung điều xe thủ công; nội dung sẽ được bổ sung ở bước tiếp theo. */
export default function DispatchModal({ open, departureDate, activeJourneyId, onClose }: DispatchModalProps) {
  const [outboundPickerDriverId, setOutboundPickerDriverId] = useState<string | null>(null);
  const [inboundPickerDriverId, setInboundPickerDriverId] = useState<string | null>(null);
  const [clearingDriverId, setClearingDriverId] = useState<string | null>(null);
  const [aiMode, setAiMode] = useState(false);
  const [aiInstruction, setAiInstruction] = useState('');
  const { addNotification } = useNotification();
  const { data: driversResponse, isLoading: loadingDrivers } = useGetUsers({
    user_role: 'driver',
    limit: 200,
    sort: 'display_name:asc',
  });
  const drivers = useMemo(() => (driversResponse?.body || []).filter((user) => user.user_role === 'driver'), [driversResponse]);

  /** --- */

  const {
    data: trip_manifest_dispatch_data,
    isLoading: trip_manifest_dispatch_data_loading,
    refetch: refetchDispatchData,
  } = useGetTrip_manifest_dispatch_list({
    departure_date: departureDate,
    journey_id: activeJourneyId,
    enabled: open,
  });
  const { mutateAsync: assignDriver, isPending: assigningDriver } = useMutateAssign_driver_to_trip();
  const { mutateAsync: removeDriver } = useMutateRemove_driver_from_trip();
  const { mutateAsync: requestAiDispatch, isPending: aiDispatching } = useRequestAiDispatch();

  const { outboundTripsByDriver, inboundTripsByDriver, assignmentIdsByDriver, unassignedOutboundTrips, unassignedInboundTrips } = useMemo(() => {
    const outboundTripsByDriver = new Map<string, string[]>();
    const inboundTripsByDriver = new Map<string, string[]>();
    const assignmentIdsByDriver = new Map<string, string[]>();
    const unassignedOutboundTrips = [] as NonNullable<typeof trip_manifest_dispatch_data>['outbound']['trips'];
    const unassignedInboundTrips = [] as NonNullable<typeof trip_manifest_dispatch_data>['inbound']['trips'];
    const addAssignedTrip = (tripsByDriver: Map<string, string[]>, driverId: string, assignmentId: string, time: string) => {
      const times = tripsByDriver.get(driverId) || [];
      times.push(time);
      tripsByDriver.set(driverId, times);
      const assignmentIds = assignmentIdsByDriver.get(driverId) || [];
      assignmentIds.push(assignmentId);
      assignmentIdsByDriver.set(driverId, assignmentIds);
    };

    (trip_manifest_dispatch_data?.outbound.trips || []).forEach((trip) => {
      if (!trip.driver?.user_id) unassignedOutboundTrips.push(trip);
      else addAssignedTrip(outboundTripsByDriver, String(trip.driver.user_id), String(trip.driver.trip_manifest_driver_id), trip.time);
    });
    (trip_manifest_dispatch_data?.inbound.trips || []).forEach((trip) => {
      if (!trip.driver?.user_id) unassignedInboundTrips.push(trip);
      else addAssignedTrip(inboundTripsByDriver, String(trip.driver.user_id), String(trip.driver.trip_manifest_driver_id), trip.time);
    });

    outboundTripsByDriver.forEach((times) => times.sort());
    inboundTripsByDriver.forEach((times) => times.sort());
    unassignedOutboundTrips.sort((tripA, tripB) => tripA.time.localeCompare(tripB.time));
    unassignedInboundTrips.sort((tripA, tripB) => tripA.time.localeCompare(tripB.time));
    return { outboundTripsByDriver, inboundTripsByDriver, assignmentIdsByDriver, unassignedOutboundTrips, unassignedInboundTrips };
  }, [trip_manifest_dispatch_data]);

  const handleAssignTrip = useCallback(
    async (tripManifestId: string, driverId: string) => {
      try {
        await assignDriver({ trip_manifest_id: tripManifestId, driver_id: driverId, driver_role: 'driver' });
        setOutboundPickerDriverId(null);
        setInboundPickerDriverId(null);
        await refetchDispatchData();
        addNotification('info', 'Đã gán tài xế cho chuyến.');
      } catch (error: any) {
        addNotification('error', error?.response?.data?.message || error?.message || 'Không thể gán tài xế cho chuyến.');
      }
    },
    [addNotification, assignDriver, refetchDispatchData]
  );

  const handleClearDriverAssignments = useCallback(
    async (driverId: string, driverName: string, assignmentIds: string[]) => {
      if (!assignmentIds.length) return;
      setClearingDriverId(driverId);
      setOutboundPickerDriverId(null);
      setInboundPickerDriverId(null);
      try {
        await Promise.all(assignmentIds.map((assignmentId) => removeDriver(assignmentId)));
        await refetchDispatchData();
        addNotification('info', `Đã xóa toàn bộ phân công của ${driverName}.`);
      } catch (error: any) {
        await refetchDispatchData();
        addNotification('error', error?.response?.data?.message || error?.message || 'Không thể xóa toàn bộ phân công của tài xế.');
      } finally {
        setClearingDriverId(null);
      }
    },
    [addNotification, refetchDispatchData, removeDriver]
  );

  const dispatchRows = useMemo(
    () =>
      drivers.map((driver) => {
        const driverId = String(driver.user_id || '');
        return {
          driver,
          outboundTimes: outboundTripsByDriver.get(driverId) || [],
          inboundTimes: inboundTripsByDriver.get(driverId) || [],
          assignmentIds: assignmentIdsByDriver.get(driverId) || [],
        };
      }),
    [assignmentIdsByDriver, drivers, inboundTripsByDriver, outboundTripsByDriver]
  );

  const handleClose = useCallback(() => {
    if (aiDispatching) return;
    setAiMode(false);
    setAiInstruction('');
    onClose();
  }, [aiDispatching, onClose]);

  const handleAiSubmit = useCallback(async () => {
    const userInstruction = aiInstruction.trim();
    if (!userInstruction) {
      addNotification('error', 'Vui lòng nhập yêu cầu điều xe.');
      return;
    }

    try {
      await requestAiDispatch({ user_instruction: userInstruction, departure_date: departureDate });
      await refetchDispatchData();
      addNotification('info', 'AI đã xử lý yêu cầu điều xe thành công.');
      setAiInstruction('');
      setAiMode(false);
    } catch (error: any) {
      addNotification('error', error?.response?.data?.message || error?.message || 'AI không thể xử lý yêu cầu điều xe.');
    }
  }, [addNotification, aiInstruction, departureDate, refetchDispatchData, requestAiDispatch]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={aiMode ? `Điều xe bằng AI ngày ${departureDate}` : `Điều tài xế ngày ${departureDate}`}
      primaryAction={
        aiMode
          ? {
              content: 'Gửi yêu cầu',
              onAction: handleAiSubmit,
              loading: aiDispatching,
              disabled: aiDispatching || !aiInstruction.trim(),
            }
          : undefined
      }
      secondaryActions={
        aiMode
          ? [{ content: 'Quay lại', onAction: () => setAiMode(false), disabled: aiDispatching }]
          : [{ content: 'Điều xe bằng AI', icon: MagicIcon, destructive: true, onAction: () => setAiMode(true) }]
      }
    >
      {aiMode ? (
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Yêu cầu điều xe"
              value={aiInstruction}
              onChange={setAiInstruction}
              multiline={6}
              autoComplete="off"
              autoFocus
              disabled={aiDispatching}
              placeholder="Ví dụ: Sắp xếp các vé chưa khóa vào những chuyến còn ghế phù hợp..."
              helpText="AI có thể cần nhiều thời gian để phân tích và thực hiện yêu cầu."
            />
            {aiDispatching ? (
              <InlineStack gap="200" blockAlign="center" wrap={false}>
                <Spinner accessibilityLabel="AI đang xử lý yêu cầu điều xe" size="small" />
                <Text as="p" tone="subdued">
                  Đang xử lý, vui lòng giữ nguyên cửa sổ và không gửi lại yêu cầu.
                </Text>
              </InlineStack>
            ) : null}
          </BlockStack>
        </Modal.Section>
      ) : (
        <Modal.Section>
          <IndexTable
            resourceName={{ singular: 'tài xế', plural: 'tài xế' }}
            itemCount={dispatchRows.length}
            selectable={false}
            loading={loadingDrivers || trip_manifest_dispatch_data_loading}
            headings={[
              { title: 'Tài xế' },
              {
                title: `Giờ đi${trip_manifest_dispatch_data?.outbound.journey.journey_name ? ` (${trip_manifest_dispatch_data.outbound.journey.journey_name})` : ''}`,
              },
              {
                title: `Giờ về${trip_manifest_dispatch_data?.inbound.journey.journey_name ? ` (${trip_manifest_dispatch_data.inbound.journey.journey_name})` : ''}`,
              },
            ]}
            emptyState={
              <Text as="p" tone="subdued">
                {loadingDrivers || trip_manifest_dispatch_data_loading ? 'Đang tải dữ liệu điều xe...' : 'Không có tài xế.'}
              </Text>
            }
          >
            {dispatchRows.map(({ driver, outboundTimes, inboundTimes, assignmentIds }, index) => {
              const driverName = driver.display_name || driver.user_login || driver.user_phonenumber || 'Chưa đặt tên';
              const driverId = String(driver.user_id || '');
              return (
                <IndexTable.Row id={driver.user_id || String(index)} key={driver.user_id || index} position={index}>
                  <IndexTable.Cell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {assignmentIds.length > 0 && (
                        <Button
                          icon={ResetIcon}
                          variant="plain"
                          tone="critical"
                          loading={clearingDriverId === driverId}
                          disabled={clearingDriverId !== null || assigningDriver}
                          onClick={() => handleClearDriverAssignments(driverId, driverName, assignmentIds)}
                          accessibilityLabel={`Xóa toàn bộ phân công của ${driverName}`}
                        />
                      )}
                      <span>{driverName}</span>
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {outboundTimes.length > 0 && <span>{outboundTimes.join(', ')}</span>}
                      <Popover
                        active={outboundPickerDriverId === driverId}
                        onClose={() => setOutboundPickerDriverId(null)}
                        activator={
                          <Button icon={PlusIcon} variant="plain" onClick={() => setOutboundPickerDriverId(driverId)} disabled={assigningDriver}>
                            {outboundTimes.length > 0 ? 'Thêm giờ đi' : 'Chọn giờ đi'}
                          </Button>
                        }
                      >
                        {unassignedOutboundTrips.length ? (
                          <div style={{ minWidth: 240 }}>
                            <ActionList
                              items={unassignedOutboundTrips.map((trip) => ({
                                content: `${trip.time}  (${trip.transportation_type})`, // trip.time + ' /' + trip.transportation_type,
                                onAction: () => handleAssignTrip(trip.trip_manifest_id, driverId),
                                disabled: assigningDriver,
                              }))}
                            />
                          </div>
                        ) : (
                          <Box padding="300">
                            <Text as="p" tone="subdued">
                              Không có giờ đi nào chưa gán tài xế.
                            </Text>
                          </Box>
                        )}
                      </Popover>
                    </div>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {inboundTimes.length > 0 && <span>{inboundTimes.join(', ')}</span>}
                      <Popover
                        active={inboundPickerDriverId === driverId}
                        onClose={() => setInboundPickerDriverId(null)}
                        activator={
                          <Button icon={PlusIcon} variant="plain" onClick={() => setInboundPickerDriverId(driverId)} disabled={assigningDriver}>
                            {inboundTimes.length > 0 ? 'Thêm giờ về' : 'Chọn giờ về'}
                          </Button>
                        }
                      >
                        {unassignedInboundTrips.length ? (
                          <div style={{ minWidth: 240 }}>
                            <ActionList
                              items={unassignedInboundTrips.map((trip) => ({
                                content: trip.time,
                                onAction: () => handleAssignTrip(trip.trip_manifest_id, driverId),
                                disabled: assigningDriver,
                              }))}
                            />
                          </div>
                        ) : (
                          <Box padding="300">
                            <Text as="p" tone="subdued">
                              Không có giờ về nào chưa gán tài xế.
                            </Text>
                          </Box>
                        )}
                      </Popover>
                    </div>
                  </IndexTable.Cell>
                </IndexTable.Row>
              );
            })}
          </IndexTable>
        </Modal.Section>
      )}
    </Modal>
  );
}
